/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import _ from 'underscore';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import OnlyClient from 'components/OnlyClient';
import CookieNotify from 'components/CookieNotify';
import SystemMessageContainer from 'containers/Layout/SystemMessage';
import HeaderContainer from 'containers/Layout/Header';
import { BodyOverflowProvider } from 'contexts/BodyOverflowContext';
import { HeaderProvider } from 'contexts/HeaderContext';
import { WrapContext, WrapProvider } from 'contexts/WrapContext';

import { getCanonicalUrl } from 'utils/helpers/url';
import CommonPropTypes from 'utils/propTypes/common';

import SvgIcons from './components/SvgIcons';
import Footer from './components/Footer';
import Meta from './components/Meta';

import styles from './styles.module.scss';

const {
	publicRuntimeConfig: { services, basePath }
} = getConfig();

const LayoutWrapper = ({ children }) => {
	const [wrapContext] = useContext(WrapContext);
	const wrapCx = cx(
		styles.wrap,
		_(wrapContext.modifiers).map((modifier) => styles[modifier])
	);

	return (
		<div id="wrap" className={wrapCx} onClick={wrapContext.onClick}>
			{children}
		</div>
	);
};

LayoutWrapper.propTypes = {
	children: PropTypes.node.isRequired
};

const IeNotifyModal = dynamic(() => import('components/modals/IeNotify'), {
	ssr: false
});

const getMetaTitleByEntityType = ({ entityType, rubricPath, entityTitle }) => {
	if (entityType !== 'movies' || !entityTitle) {
		return null;
	}
	const rubricPathParts = rubricPath.split('/') || [];
	const subTypeKey = rubricPathParts.find((part) =>
		['movies', 'concerts', 'lectures', 'performances'].includes(part)
	);

	const entityTypeTitleHash = {
		movies: 'фильм',
		concerts: 'концерт',
		lectures: 'лекцию',
		performances: 'спектакль'
	};
	const entityTypeTitle = entityTypeTitleHash[subTypeKey];
	if (!entityTypeTitle) {
		return null;
	}

	return `Смотреть ${entityTypeTitle} «${entityTitle}» в хорошем качестве на портале «Культура.РФ».`;
};

const DEFAULT_META_TITLE =
	'Прямые трансляции: смотреть онлайн и бесплатно трансляции и записи концертов, лекций, фестивалей в разделе «Культурный стриминг»';

const Layout = ({
	children,
	title,
	rubric,
	meta,
	entity,
	entityType,
	rubricPath,
	noCover,
	noIndex,
	theme,
	preloadImageSrc
}) => {
	const [isBrowserIe, setIsBrowserIe] = useState(false);
	const { asPath: currentPath } = useRouter();
	const customMeta = meta || (rubric && rubric.meta);
	const mainCx = cx(styles.main, styles[`theme_${theme}`], {
		[styles.noCover]: noCover
	});
	const canonicalUrl = getCanonicalUrl({ entity, entityType, currentPath });

	useEffect(() => {
		setIsBrowserIe(/msie|trident/i.test(window.navigator.userAgent));
	}, []);

	const entityMetaTitle = getMetaTitleByEntityType({
		entityType,
		rubricPath,
		entityTitle: entity?.title
	});

	const metaTitle =
		title || entityMetaTitle || customMeta?.title || DEFAULT_META_TITLE;

	return (
		<>
			<Head>
				<title>{metaTitle}</title>

				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta httpEquiv="X-UA-Compatible" content="IE=edge" />
				<meta name="format-detection" content="telephone=no" />
				{noIndex && <meta name="robots" content="noindex, nofollow" />}
				<meta
					name="proculture-verification"
					content={services.pixel.proVerificationId}
				/>
				<meta name="yandex-verification" content={services.yandex.webmaster} />

				<meta
					name="google-site-verification"
					content={services.google.searchConsoleKey}
				/>

				<link rel="canonical" href={canonicalUrl} />
				<link
					rel="apple-touch-icon"
					sizes="180x180"
					href={`${basePath}/favicons/apple-touch-icon.png`}
				/>
				<link
					rel="icon"
					type="image/png"
					sizes="32x32"
					href={`${basePath}/favicons/favicon-32x32.png`}
				/>
				<link
					rel="icon"
					type="image/png"
					sizes="194x194"
					href={`${basePath}/favicons/favicon-194x194.png`}
				/>
				<link
					rel="icon"
					type="image/png"
					sizes="192x192"
					href={`${basePath}/favicons/android-chrome-192x192.png`}
				/>
				<link
					rel="icon"
					type="image/png"
					sizes="16x16"
					href={`${basePath}/favicons/favicon-16x16.png`}
				/>
				<link rel="manifest" href={`${basePath}/favicons/site.webmanifest`} />
				<link
					rel="mask-icon"
					href={`${basePath}/favicons/safari-pinned-tab.svg`}
					color="#7a7e7f"
				/>
				<link
					rel="preload"
					href={`${basePath}/fonts/Roboto/Roboto-Regular.woff`}
					as="font"
					type="font/woff"
					crossOrigin="anonymous"
				/>
				<link
					rel="preload"
					href={`${basePath}/fonts/PTSans/PTSansRegular.woff`}
					as="font"
					type="font/woff"
					crossOrigin="anonymous"
				/>
				{preloadImageSrc && (
					<link rel="preload" href={preloadImageSrc} as="image" />
				)}

				<meta name="msapplication-TileColor" content="#7a7e7f" />
				<meta
					name="msapplication-config"
					content={`${basePath}/favicons/browserconfig.xml`}
				/>
				<meta name="theme-color" content="#ffffff" />
			</Head>
			{customMeta && (
				<Meta
					meta={customMeta}
					rubric={rubric}
					entity={entity}
					entityType={entityType}
				/>
			)}

			<BodyOverflowProvider>
				<WrapProvider>
					<LayoutWrapper>
						<HeaderProvider>
							<HeaderContainer rubricPath={rubricPath} />

							<main className={mainCx}>{children}</main>

							<Footer />

							<SvgIcons />

							<OnlyClient>
								<SystemMessageContainer />
							</OnlyClient>

							<OnlyClient>
								<CookieNotify />
							</OnlyClient>

							{isBrowserIe && <IeNotifyModal />}
						</HeaderProvider>
					</LayoutWrapper>
				</WrapProvider>
			</BodyOverflowProvider>
		</>
	);
};

Layout.propTypes = {
	title: PropTypes.string,
	rubric: CommonPropTypes.rubric,
	rubricPath: PropTypes.string,
	meta: CommonPropTypes.meta,
	entity: CommonPropTypes.entity,
	entityType: CommonPropTypes.entityType,
	children: PropTypes.node.isRequired,
	noCover: PropTypes.bool,
	noIndex: PropTypes.bool,
	theme: PropTypes.oneOf(['flex']),
	preloadImageSrc: PropTypes.string
};

Layout.defaultProps = {
	noCover: false,
	noIndex: false
};

export default Layout;
